import apiClient from '../../../../api/axios';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  TDiscountsResponse,
  TDiscountResponse,
  TCreateDiscountRequest,
  TUpdateDiscountRequest,
} from '../types';

const DISCOUNTS_BASE_URL = '/discount';
const CATEGORIES_BASE_URL = '/menu/category/all';
const QUERY_KEY = 'discounts';
const CATEGORIES_QUERY_KEY = 'categories';

export type TCategory = {
  _id: string;
  name: string;
  order?: number;
};

type TCategoriesResponse = {
  success: boolean;
  data: {
    rows: TCategory[];
  };
};

export const useGetCategories = (restaurantId: string) => {
  return useQuery({
    queryKey: [CATEGORIES_QUERY_KEY, restaurantId],
    queryFn: async () => {
      const response = await apiClient.get<TCategoriesResponse>(CATEGORIES_BASE_URL, {
        headers: {
          'Restaurant-ID': restaurantId,
        },
      });
      const rows = response.data?.data?.rows || [];
      return rows.sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0));
    },
    enabled: !!restaurantId,
  });
};

export const useGetDiscounts = (restaurantId: string, pageNum = 1, pageSize = 10) => {
  return useQuery({
    queryKey: [QUERY_KEY, restaurantId, pageNum, pageSize],
    queryFn: async () => {
      const response = await apiClient.get<TDiscountsResponse>(
        `${DISCOUNTS_BASE_URL}?pageNum=${pageNum}&pageSize=${pageSize}`,
        {
          headers: {
            'Restaurant-ID': restaurantId,
          },
        },
      );
      return response.data?.data;
    },
    enabled: !!restaurantId,
  });
};

export const useGetDiscount = (id: string, restaurantId: string) => {
  return useQuery({
    queryKey: [QUERY_KEY, id],
    queryFn: async () => {
      const response = await apiClient.get<TDiscountResponse>(`${DISCOUNTS_BASE_URL}/${id}`, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data?.data;
    },
    enabled: !!id && !!restaurantId,
  });
};

export const useCreateDiscount = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: TCreateDiscountRequest) => {
      const response = await apiClient.post<TDiscountResponse>(DISCOUNTS_BASE_URL, payload, {
        headers: {
          'Restaurant-ID': restaurantId,
        },
      });
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateDiscount = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: TUpdateDiscountRequest }) => {
      const response = await apiClient.put<TDiscountResponse>(`${DISCOUNTS_BASE_URL}/${id}`, payload, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data?.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useDeleteDiscount = (restaurantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await apiClient.put<TDiscountResponse>(`${DISCOUNTS_BASE_URL}/${id}`, {
        status: 'disabled',
      }, {
        headers: { 'Restaurant-ID': restaurantId },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
